/**
 * VH-253: TanStack Query hooks for the shopping list.
 *
 * Ticking is optimistic on purpose. It happens one-handed, standing in an aisle, and
 * waiting for a round trip on a shop's one bar of signal makes every tap feel broken.
 * The trade is that a failed save must be visible and must put the row back exactly
 * where it was — so `onMutate` snapshots the whole list payload and `onError` restores
 * it, and the page shows the failure rather than swallowing it.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { shoppingAPI } from '../services/shopping'

export const shoppingKeys = {
  all: ['shopping'],
  list: () => [...shoppingKeys.all, 'list'],
}

export function useShoppingList() {
  return useQuery({
    queryKey: shoppingKeys.list(),
    queryFn: () => shoppingAPI.list().then((r) => r.data),
    staleTime: 1000 * 15,
  })
}

/**
 * Move one item between the open groups and the bought section, in the cache.
 *
 * Returns a NEW payload; never mutates the cached one. The aisle an item returns to on
 * an untick is its own `group`, which the row carries — that is why the list payload
 * keeps `group` on every item and not just on the section.
 */
export function applyTick(list, itemId, bought) {
  if (!list) return list

  const groups = list.groups || []
  const boughtRows = list.bought || []

  let moved = null
  const remainingGroups = groups
    .map((g) => ({
      ...g,
      items: g.items.filter((item) => {
        if (item.id !== itemId) return true
        moved = item
        return false
      }),
    }))
    .filter((g) => g.items.length > 0)

  if (bought) {
    if (!moved) return list
    return {
      ...list,
      open_count: Math.max(0, (list.open_count || 0) - 1),
      groups: remainingGroups,
      bought: [{ ...moved, bought_at: new Date().toISOString() }, ...boughtRows],
    }
  }

  const returning = boughtRows.find((item) => item.id === itemId)
  if (!returning) return list
  const restored = { ...returning, bought_at: null }
  const target = remainingGroups.find((g) => g.group === restored.group)
  const openGroups = target
    ? remainingGroups.map((g) =>
        g.group === restored.group
          ? { ...g, items: [...g.items, restored].sort((a, b) => a.name.localeCompare(b.name)) }
          : g
      )
    : [...remainingGroups, { group: restored.group, items: [restored] }]

  return {
    ...list,
    open_count: (list.open_count || 0) + 1,
    groups: openGroups,
    bought: boughtRows.filter((item) => item.id !== itemId),
  }
}

export function useSetBought() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, bought }) => shoppingAPI.setBought(id, bought).then((r) => r.data),
    onMutate: async ({ id, bought }) => {
      await queryClient.cancelQueries({ queryKey: shoppingKeys.list() })
      const previous = queryClient.getQueryData(shoppingKeys.list())
      queryClient.setQueryData(shoppingKeys.list(), (list) => applyTick(list, id, bought))
      return { previous }
    },
    onError: (_error, _vars, context) => {
      // The save failed: the item stays exactly as it was, and the page says so.
      if (context?.previous !== undefined) {
        queryClient.setQueryData(shoppingKeys.list(), context.previous)
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: shoppingKeys.list() }),
  })
}

export function useAddItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data) => shoppingAPI.addItem(data).then((r) => r.data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: shoppingKeys.list() }),
  })
}

export function useImportPaste() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (text) => shoppingAPI.paste(text).then((r) => r.data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: shoppingKeys.list() }),
  })
}

export function useAddFromPlan() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (cycleId) => shoppingAPI.fromPlan(cycleId).then((r) => r.data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: shoppingKeys.list() }),
  })
}

/** VH-260: the recipe's "Add to shopping list" button. */
export function useAddFromRecipe() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ recipeId, versionId, scale }) =>
      shoppingAPI.fromRecipe(recipeId, versionId, scale).then((r) => r.data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: shoppingKeys.list() }),
  })
}

export function useRemoveItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id) => shoppingAPI.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: shoppingKeys.list() }),
  })
}

export function useClearBought() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => shoppingAPI.clearBought().then((r) => r.data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: shoppingKeys.list() }),
  })
}
